import { useAuthContext } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { clearStaleAuthStorage } from '@/lib/auth-utils';

interface SignupOptions {
  referralCode?: string;
  source?: string;
  medium?: string;
  campaign?: string;
}

/**
 * Auth hook that wraps AuthProvider context and adds sign-in/sign-up methods.
 * All auth state comes from the single AuthProvider - no duplicate listeners.
 */
export function useAuth() {
  const context = useAuthContext();

  const signUp = async (
    email: string, 
    password: string, 
    fullName?: string,
    options?: SignupOptions
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: fullName ? { full_name: fullName } : undefined,
      },
    });

    // If signup successful and we have attribution data, update the profile
    if (data?.user && !error) {
      await processSignupAttribution(data.user.id, options);
    }

    return { data, error };
  };

  const processSignupAttribution = async (userId: string, options?: SignupOptions) => {
    if (!options) return;

    try {
      // Update profile with attribution data
      if (options.source || options.medium || options.campaign) {
        await supabase
          .from('profiles')
          .update({
            signup_source: options.source,
            signup_medium: options.medium,
            signup_campaign: options.campaign,
          })
          .eq('id', userId);
      }

      // Process referral code
      if (options.referralCode) {
        // Look up the referrer by their referral code
        const { data: referrer } = await supabase
          .from('profiles')
          .select('id')
          .eq('referral_code', options.referralCode.toUpperCase())
          .single();

        if (referrer && referrer.id !== userId) {
          // Create referral record (trigger will award entries when marked complete)
          await supabase
            .from('referrals')
            .insert({
              referrer_id: referrer.id,
              referred_id: userId,
              referral_code: options.referralCode.toUpperCase(),
              referrer_entries_awarded: 25,
              referred_entries_awarded: 25,
              is_completed: true,
            });

          // Update the new user's referred_by field
          await supabase
            .from('profiles')
            .update({ referred_by: referrer.id })
            .eq('id', userId);
        }
      }
    } catch (error) {
      console.error('Error processing signup attribution:', error);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    return { data, error };
  };

  const sendOtp = async (email: string, shouldCreateUser = true) => {
    const { data, error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        shouldCreateUser,
      }
    });
    return { data, error };
  };

  const verifyOtp = async (email: string, token: string) => {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    });
    return { data, error };
  };

  const signOut = async () => {
    // Clear any stale auth storage
    clearStaleAuthStorage();
    
    // Use the context signOut which handles state cleanup
    await context.signOut();
    
    return { error: null };
  };

  return {
    ...context,
    signUp,
    signIn,
    signOut,
    sendOtp,
    verifyOtp,
  };
}
